import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import useCrmInteraction from 'api/hooks/useCrmInteraction';
import { BREAKDOWN_OPTIONS } from './breakdownDimensions';

// Matches useCrmInteractionsQuery.js's own page size for the same endpoint.
const PAGE_SIZE = 10;

const unitForView = (currentView) => (currentView === 'Day' ? 'day' : currentView === 'Week' ? 'week' : 'month');

const buildFilters = ({ cell, breakdownBy, currentView }) => {
  const filters = [
    { field: 'dateTime', operator: 'date_gte', value: cell.date },
    { field: 'dateTime', operator: 'date_lt', value: dayjs(cell.date).add(1, unitForView(currentView)).format('YYYY-MM-DD') },
  ];
  if (cell.bucket != null) {
    const filterField = BREAKDOWN_OPTIONS.find((option) => option.value === breakdownBy)?.filterField;
    if (filterField) {
      filters.push({ field: filterField, operator: 'str_eq', value: `${cell.bucket}` });
    }
  }
  return filters;
};

// Mirrors useCrmInteractionsQuery.js's own initial-fetch/load-more-page split for the same endpoint,
// scaled down for this panel: no cache, no local add/update/remove mutation (this list is a read-only
// snapshot for one chart cell, not the editable main Interactions list).
const useCallInteractionsForCell = ({ cell, breakdownBy, currentView }) => {
  const { queryCrmInteractions } = useCrmInteraction();
  const [interactions, setInteractions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingScroll, setIsLoadingScroll] = useState(false);
  const [thereIsMoreContent, setThereIsMoreContent] = useState(false);

  const pageRef = useRef(1);
  const loadMoreSourceRef = useRef(null);
  const initialCall = useRef(null);
  const loadMoreCall = useRef(null);

  initialCall.current = async (signal) => {
    if (!cell) return;
    setIsLoading(true);
    try {
      const filters = buildFilters({ cell, breakdownBy, currentView });
      const result = await queryCrmInteractions({ signal, filters, pageNumber: 1, pageSize: PAGE_SIZE });
      if (signal.aborted) return;
      if (result) {
        setInteractions(result.hits ?? []);
        setThereIsMoreContent((result.hits ?? []).length === PAGE_SIZE);
      }
    } catch (error) {}
    setIsLoading(false);
  };

  loadMoreCall.current = async (signal, pageNumber) => {
    setIsLoadingScroll(true);
    try {
      const filters = buildFilters({ cell, breakdownBy, currentView });
      const result = await queryCrmInteractions({ signal, filters, pageNumber, pageSize: PAGE_SIZE });
      if (signal.aborted) return;
      if (result) {
        setInteractions((prev) => [...(prev ?? []), ...(result.hits ?? [])]);
        setThereIsMoreContent((result.hits ?? []).length === PAGE_SIZE);
      }
    } catch (error) {}
    setIsLoadingScroll(false);
  };

  const handleScrollBottom = () => {
    if (isLoadingScroll || !thereIsMoreContent) return;
    loadMoreSourceRef.current?.abort();
    loadMoreSourceRef.current = new AbortController();
    pageRef.current += 1;
    loadMoreCall.current(loadMoreSourceRef.current.signal, pageRef.current);
  };

  useEffect(() => {
    const controller = new AbortController();
    loadMoreSourceRef.current?.abort();
    pageRef.current = 1;
    initialCall.current(controller.signal);
    return () => controller.abort('Component unmounted.');
  }, [cell?.date, cell?.bucket, breakdownBy, currentView]);

  return { interactions, isLoading, isLoadingScroll, thereIsMoreContent, handleScrollBottom };
};

export default useCallInteractionsForCell;
